import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import { fetchDeviceInventory, type DeviceRow } from "../data/devices";
import {
  restoreAccountDevice,
  revokeAccountDevice,
  SAFE_DEVICE_TRUST_FAILURE,
} from "../data/member-lifecycle";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";
import { guardRefTitle, resolveGuardName } from "../lib/guard-name";

// Device Inventory — enrolled devices linked to account members by user_id. Shows member
// display name + email when known; device_label stays redacted. With canManage (display
// gate can("schedule.manage"), the same admin+supervisor audience the hosted device-trust
// RPCs enforce by role), operators drive Wave B revoke/restore: revoke requires a reason,
// retires that device's push tokens in the same transaction, and blocks the device at its
// next token registration or scan sync. Restore never resurrects push tokens (the device
// re-registers on next app start). The RPCs are the only mutation path; no table DML.

export function DeviceInventory({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
  canManage = false,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  canManage?: boolean;
}) {
  const { status, data, error, reload } = useReadModel<DeviceRow[]>(
    () => fetchDeviceInventory(client, accountId),
    [client, accountId],
    pollMs,
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading device inventory…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load device inventory."}
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return <p className="surface__state" role="status">No enrolled devices yet.</p>;
  }

  return (
    <table className="devices">
      <thead>
        <tr>
          <th scope="col">Device</th>
          <th scope="col">Member</th>
          <th scope="col">Platform</th>
          <th scope="col">Status</th>
          <th scope="col">App</th>
          <th scope="col">Last seen</th>
          {canManage ? <th scope="col">Trust</th> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <DeviceInventoryRow
            key={row.deviceRowId || `${row.deviceLabel ?? "device"}-${index}`}
            row={row}
            timeZone={timeZone}
            canManage={canManage}
            client={client}
            accountId={accountId}
            onChanged={reload}
          />
        ))}
      </tbody>
    </table>
  );
}

function DeviceInventoryRow({
  row,
  timeZone,
  canManage,
  client,
  accountId,
  onChanged,
}: {
  row: DeviceRow;
  timeZone: string;
  canManage: boolean;
  client: SupabaseClient;
  accountId: string;
  onChanged: () => void;
}) {
  const effectiveStatus = row.isRevoked ? "revoked" : row.status;
  const member = resolveGuardName({
    displayName: row.memberDisplayName,
    label: row.userRef,
    ref: row.userRef?.replace(/^User /i, "") ?? null,
    fallback: row.userRef ?? "Unknown member",
  });
  const memberAnchor = row.membershipId !== null ? `#member-${row.membershipId}` : undefined;

  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [revokeReason, setRevokeReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);

  async function runRevoke(): Promise<void> {
    if (busy || row.deviceRowId.length === 0) return;
    setBusy(true);
    setActionError(null);
    setActionNote(null);
    try {
      const outcome = await revokeAccountDevice(client, accountId, row.deviceRowId, revokeReason);
      if (!outcome.ok) {
        setActionError(outcome.reason);
        return;
      }
      setActionNote(
        `Device revoked; ${outcome.pushTokensRetired} push token${outcome.pushTokensRetired === 1 ? "" : "s"} retired. It is blocked at its next registration or scan sync.`,
      );
      setConfirmingRevoke(false);
      setRevokeReason("");
      onChanged();
    } catch {
      setActionError(SAFE_DEVICE_TRUST_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  async function runRestore(): Promise<void> {
    if (busy || row.deviceRowId.length === 0) return;
    setBusy(true);
    setActionError(null);
    setActionNote(null);
    try {
      const outcome = await restoreAccountDevice(client, accountId, row.deviceRowId);
      if (!outcome.ok) {
        setActionError(outcome.reason);
        return;
      }
      setActionNote("Device trusted again. It re-registers push on next app start.");
      onChanged();
    } catch {
      setActionError(SAFE_DEVICE_TRUST_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="devices__row">
      <td className="devices__device">{row.deviceLabel ?? "Device"}</td>
      <td className="devices__user">
        {row.membershipId !== null && memberAnchor !== undefined ? (
          <a className="devices__member-link" href={memberAnchor} title={guardRefTitle(member.ref)}>
            <span className="devices__member-name">{member.name}</span>
            {row.memberEmail !== null ? (
              <span className="devices__member-email">{row.memberEmail}</span>
            ) : null}
            {row.memberContactPhone !== null ? (
              <span className="devices__member-phone">{row.memberContactPhone}</span>
            ) : null}
          </a>
        ) : (
          <span title={guardRefTitle(member.ref)}>{member.name}</span>
        )}
      </td>
      <td className="devices__platform">
        {row.platform === null ? "—" : humanizeLabel(row.platform)}
      </td>
      <td>
        <span className={`devices__status devices__status--${effectiveStatus}`}>
          {humanizeLabel(effectiveStatus)}
        </span>
        {row.isRevoked && row.revokeReason !== null ? (
          <span className="devices__reason">{humanizeLabel(row.revokeReason)}</span>
        ) : null}
      </td>
      <td className="devices__app">{row.lastAppVersion ?? "—"}</td>
      <td className="devices__seen">{formatDisplayTime(row.lastSeenAt, timeZone)}</td>
      {canManage ? (
        <td className="devices__trust-actions">
          {confirmingRevoke ? (
            <div className="devices__revoke-confirm">
              <p className="devices__revoke-copy">
                Revoking blocks this device at its next registration or scan sync and retires
                its push tokens. A reason is required.
              </p>
              <input
                type="text"
                value={revokeReason}
                onChange={(event) => setRevokeReason(event.target.value)}
                placeholder="Reason (required)"
                aria-label={`Reason for revoking ${row.deviceLabel ?? "device"}`}
                disabled={busy}
              />
              <button
                type="button"
                className="builder__cancel"
                disabled={busy || revokeReason.trim().length === 0}
                onClick={() => void runRevoke()}
              >
                {busy ? "Working…" : "Confirm revoke"}
              </button>
              <button
                type="button"
                className="builder__navbtn"
                disabled={busy}
                onClick={() => {
                  setConfirmingRevoke(false);
                  setRevokeReason("");
                  setActionError(null);
                }}
              >
                Keep trusted
              </button>
            </div>
          ) : effectiveStatus === "revoked" ? (
            <button type="button" className="builder__navbtn" disabled={busy} onClick={() => void runRestore()}>
              Restore
            </button>
          ) : (
            <button
              type="button"
              className="builder__cancel"
              disabled={busy || row.deviceRowId.length === 0}
              onClick={() => {
                setActionNote(null);
                setActionError(null);
                setConfirmingRevoke(true);
              }}
            >
              Revoke
            </button>
          )}
          {actionError !== null ? (
            <p className="surface__state surface__state--error" role="alert">{actionError}</p>
          ) : null}
          {actionNote !== null ? (
            <p className="surface__state" role="status">{actionNote}</p>
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}
